import {
  Box,
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogPositioner,
  DialogRoot,
  Flex,
  Portal,
  Text,
  chakra,
} from "@chakra-ui/react";
import { Link } from "../Link/Link";
import { Icon } from "../../components/Icon/Icon";

interface GroupMeInfoDialogProps {
  isShown: boolean;
  onCloseComplete?: () => void;
}

const Step = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize="14px" color="fg.default" lineHeight="1.55" mt="3">
    {children}
  </Text>
);

export const GroupMeInfoDialog = ({
  isShown,
  onCloseComplete,
}: GroupMeInfoDialogProps) => {
  const close = () => onCloseComplete?.();
  return (
    <DialogRoot
      open={isShown}
      onOpenChange={(details) => {
        if (!details.open) close();
      }}
      size="md"
      placement="center"
      motionPreset="slide-in-bottom"
    >
      <Portal>
        <DialogBackdrop zIndex={2000} />
        <DialogPositioner zIndex={2001}>
          <DialogContent
            w={{ base: "calc(100% - 2rem)", md: "30rem" }}
            maxW="30rem"
            mx="auto"
            borderRadius="md"
            bg="bg.canvas"
            display="flex"
            flexDirection="column"
            overflow="hidden"
          >
            <Flex
              align="center"
              justify="space-between"
              px="5"
              py="3"
              borderBottom="1px solid"
              borderColor="border.subtle"
            >
              <Text
                fontSize="11px"
                fontWeight="bold"
                color="fg.muted"
                letterSpacing="0.14em"
                textTransform="uppercase"
              >
                How to Connect via GroupMe
              </Text>
              <chakra.button
                type="button"
                onClick={close}
                aria-label="Close"
                bg="transparent"
                border="0"
                p="1"
                cursor="pointer"
                color="fg.muted"
                _hover={{ color: "fg.default" }}
              >
                <Icon name="x" boxSize="14px" />
              </chakra.button>
            </Flex>
            <DialogBody p="5">
              <Text fontSize="14px" color="fg.default" lineHeight="1.55">
                If you prefer not to share any information, you can connect
                through the Green Flag GroupMe channel.{" "}
                <chakra.strong fontWeight="semibold">
                  GroupMe doesn't show your phone number to connections.
                </chakra.strong>
              </Text>
              <Text
                fontSize="14px"
                color="fg.default"
                lineHeight="1.55"
                mt="4"
                fontWeight="semibold"
              >
                To connect with matches on GroupMe:
              </Text>
              <Step>
                1. Download{" "}
                <Link to="https://groupme.com/" target="_blank">
                  GroupMe app
                </Link>{" "}
                if you don't already have it.
              </Step>
              <Step>
                2. Join the{" "}
                <Link
                  to="https://groupme.com/join_group/101894042/TX8d8SPk"
                  target="_blank"
                >
                  Green Flag Announcements Channel
                </Link>
              </Step>
              <Step>3. Click on the channel name.</Step>
              <Step>4. View the list of members.</Step>
              <Step>5. Click on your match's name to direct message them.</Step>
              <Box display="flex" justifyContent="center" mt="6">
                <chakra.button
                  type="button"
                  onClick={close}
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  bg="bg.brand"
                  color="fg.onBrand"
                  border="0"
                  borderRadius="pill"
                  minH="48px"
                  py="3"
                  px="8"
                  fontWeight="bold"
                  fontSize="14px"
                  lineHeight="1"
                  cursor="pointer"
                  boxShadow="0 4px 12px rgba(57,184,125,0.3)"
                  _hover={{ filter: "brightness(1.04)" }}
                >
                  Done
                </chakra.button>
              </Box>
            </DialogBody>
          </DialogContent>
        </DialogPositioner>
      </Portal>
    </DialogRoot>
  );
};
